import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import {
    getCashCycles,
    getCurrentCashCycle,
    createCashCycle,
    closeCashCycle
} from '../../../store/actions/financialActions';
import {
    Box,
    Typography,
    Button,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CycleDetailsDialog from './CycleDetailsDialog';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

const CashCycles: React.FC = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const cashCycles = useSelector((state: RootState) => state.financial.cashCycles);
    const currentCashCycle = useSelector((state: RootState) => state.financial.currentCashCycle);
    const isFetchingCycles = useSelector((state: RootState) => state.financial.isFetchingCycles);
    const isSavingCycle = useSelector((state: RootState) => state.financial.isSavingCycle);

    const [openDialog, setOpenDialog] = useState(false);
    const [closeDialog, setCloseDialog] = useState(false);
    const [initialValue, setInitialValue] = useState('');
    const [closingCycleId, setClosingCycleId] = useState<number | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsCycleId, setDetailsCycleId] = useState<number | null>(null);
    const [detailsCycleLote, setDetailsCycleLote] = useState<number | undefined>(undefined);

    useEffect(() => {
        // @ts-ignore
        dispatch(getCashCycles());
        // @ts-ignore
        dispatch(getCurrentCashCycle());
    }, [dispatch]);

    const handleOpenCycle = () => {
        const value = parseFloat(initialValue);
        if (isNaN(value) || value < 0) {
            enqueueSnackbar('Valor inicial inválido', { variant: 'error' });
            return;
        }
        // @ts-ignore
        dispatch(createCashCycle({
            StartDate: new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }),
            Status: 1,
            InitialValueInCash: value
        }));
        enqueueSnackbar('Ciclo de caixa aberto com sucesso', { variant: 'success' });
        setOpenDialog(false);
        setInitialValue('');
    };

    const handleCloseCycle = () => {
        if (closingCycleId) {
            // @ts-ignore
            dispatch(closeCashCycle(closingCycleId, {
                EndDate: new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }),
                Status: 0
            }));
            enqueueSnackbar('Ciclo de caixa fechado com sucesso', { variant: 'success' });
        }
        setCloseDialog(false);
        setClosingCycleId(null);
    };

    const handleRequestClose = (cycleId: number) => {
        setClosingCycleId(cycleId);
        setCloseDialog(true);
    };

    const handleOpenDetails = (cycleId: number, lote: number) => {
        setDetailsCycleId(cycleId);
        setDetailsCycleLote(lote);
        setDetailsOpen(true);
    };

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'id',
            label: 'Lote',
            options: { filter: true, sort: true }
        },
        {
            name: 'StartDate',
            label: 'Início',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
        {
            name: 'EndDate',
            label: 'Fim',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
        {
            name: 'Status',
            label: 'Status',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: number) => (
                    <Chip
                        label={value === 1 ? 'Aberto' : 'Fechado'}
                        color={value === 1 ? 'success' : 'default'}
                        size="small"
                    />
                )
            }
        },
        {
            name: 'InitialValueInCash',
            label: 'Valor Inicial',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => formatBRL(value || 0)
            }
        },
        {
            name: 'id',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const cycle = cashCycles[dataIndex];
                    if (!cycle) return null;
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDetails(cycle.id, cycle.id);
                                }}
                            >
                                Detalhes
                            </Button>
                            {cycle.Status === 1 && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestClose(cycle.id);
                                    }}
                                >
                                    Fechar Ciclo
                                </Button>
                            )}
                        </Box>
                    );
                }
            }
        }
    ];

    const tableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        download: false,
        print: false,
        textLabels: {
            body: {
                noMatch: 'Sem registros',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                viewColumns: 'Ver colunas',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Ciclos de Caixa</Typography>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!!currentCashCycle || isSavingCycle}
                        onClick={() => setOpenDialog(true)}
                    >
                        Abrir Ciclo
                    </Button>
                </Box>
            </Box>

            {currentCashCycle && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderLeft: '4px solid #4caf50' }}>
                    <Typography variant="h6" gutterBottom>Ciclo Atual - Lote #{currentCashCycle.id}</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Aberto em</Typography>
                                    <Typography variant="h6">{formatDate(currentCashCycle.StartDate)}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Valor Inicial</Typography>
                                    <Typography variant="h6">{formatBRL(currentCashCycle.InitialValueInCash || 0)}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Status</Typography>
                                    <Chip label="Aberto" color="success" />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {isFetchingCycles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={2}>
                    <MUIDataTable
                        title="Histórico de Ciclos"
                        data={cashCycles}
                        columns={columns}
                        options={tableOptions}
                    />
                </Paper>
            )}

            {/* Dialog: Abrir Ciclo */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Abrir Novo Ciclo de Caixa</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Valor Inicial em Caixa (R$)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={initialValue}
                        onChange={(e) => setInitialValue(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleOpenCycle} variant="contained" disabled={isSavingCycle}>
                        Abrir Ciclo
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Fechar Ciclo */}
            <Dialog open={closeDialog} onClose={() => setCloseDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirmar Fechamento de Ciclo</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja fechar o ciclo de caixa #{closingCycleId}?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Esta ação registrará a data/hora atual como fim do ciclo.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCloseDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCloseCycle} variant="contained" color="error" disabled={isSavingCycle}>
                        Fechar Ciclo
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog: Detalhes do Ciclo */}
            <CycleDetailsDialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                cycleId={detailsCycleId}
                cycleLote={detailsCycleLote}
            />
        </Box>
    );
};

export default CashCycles;
